import { useState } from "react";
import { Badge, Button, Callout, Card, DataTable, FormField, Modal, useToast } from "@promptgatewaysadmin/ui";
import { usePolicy, usePolicyAudit, useResetPolicy, useSavePolicy, useSubscriptionState } from "../../queries/hooks.js";
import { ErrorState, Skeleton, toPortalError } from "../../components/states.js";
import { errorCopy } from "../../copy/errors.js";
import { DateText } from "../../components/format.js";
import { config } from "../../config.js";
import type { GetOrgPolicyResponse } from "../../api/generated/policy.generated.js";

/**
 * /team/policy — a CURATED KEY ALLOW-LIST, not a document editor.
 *
 * PolicyDocument has 12 top-level members and PolicyOverrides accepts 8. Two tempting
 * designs are both wrong: a form over every key is a config console that exposes
 * refreshIntervalSec and the three-tier `actions` base to an admin who has no way to reason
 * about them; and a JSON textarea hands an admin a validated way to lock their own org out,
 * turning a 400 into a debugging session.
 *
 * So v1 ships the eight keys below. Each is a lever a customer has an opinion about, each is
 * in PolicyOverrides, and each maps to something already promised on the marketing site or
 * in the Team callout. Adding a key later is additive and cheap; removing one after admins
 * have pinned it is not.
 *
 * Deliberately OUT of v1: `actions` (server-owned semantics across three tiers — the easiest
 * way to disable something you depend on, with no preview of the effect), `clarify` and
 * `caching` (pipeline tuning, not policy), `allowedDomains` (only meaningful for an action
 * the portal does not explain), `refreshIntervalSec` (an ops knob wearing a customer's
 * clothes), and the five server-owned fields the contract says MUST NOT appear in overrides.
 */

type Draft = {
  redactionLevel?: string;
  guardMandatory?: boolean;
  modelsAllowed?: string[];
  modelsDefault?: string;
  providersAllowed?: string[];
};

export function PolicyEditor() {
  const policy = usePolicy();
  const sub = useSubscriptionState();
  const save = useSavePolicy();
  const reset = useResetPolicy();
  const toast = useToast();

  const [draft, setDraft] = useState<Draft>({});
  const [showHistory, setShowHistory] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const doc = policy.data;
  const state = sub.data;
  const guard = state?.products.includes("guard") ?? false;
  // Provider restrictions are an Enterprise capability (Business is gone as of 2026-09-02).
  const enterprise = state?.plan === "enterprise";
  const locked = state?.status === "locked";
  // The read path fails SAFE: a document may arrive with degraded: true, meaning it was
  // served from a fallback tier because the authoritative store was unavailable. That is not
  // an error — but it must not be edited blind.
  const degraded = doc?.degraded === true;

  const readOnly = locked || degraded;
  const changeCount = Object.keys(draft).length;

  const failure = toPortalError(save.error);
  const conflict = failure?.copyKey === "error.policy.versionConflict";

  if (policy.isError) {
    return (
      <div className="pg-page pg-page--narrow">
        <ErrorState error={policy.error} fallbackKey="error.policy.load" onRetry={() => void policy.refetch()} />
      </div>
    );
  }
  if (policy.isPending || !doc) {
    return (
      <div className="pg-page pg-page--narrow">
        <Skeleton height={320} />
      </div>
    );
  }

  return (
    <div className="pg-page pg-page--narrow">
      <p className="pg-page__sub">
        Applies to every member of your team, on every surface.
      </p>

      {degraded && (
        <Callout tone="warn" title="Showing a cached policy" role="status">
          We couldn&apos;t reach the policy service, so this may not be current. Editing is
          paused until it&apos;s back.{" "}
          <Button size="sm" variant="ghost" onClick={() => void policy.refetch()}>
            Retry
          </Button>
        </Callout>
      )}

      {locked && (
        <Callout tone="warn" title="Your trial has ended" role="status">
          Your policy is read-only until you choose a plan. Nothing changed — your team is
          still on these settings.
        </Callout>
      )}

      {/* A 409 announces ASSERTIVELY, because it means the user's next action changes. */}
      {conflict && <ConflictNotice doc={doc} onReview={() => void policy.refetch()} />}

      {failure && !conflict && (
        <Callout tone="danger" role="alert">
          {errorCopy(failure.copyKey).body}{" "}
          <Button size="sm" variant="ghost" onClick={() => save.reset()}>
            Retry
          </Button>
        </Callout>
      )}

      <Section title="Data protection">
        {!guard && (
          <Callout tone="info">
            PII redaction is part of Prompt Guard.{" "}
            <a href={`${config.siteOrigin}/contact-sales`}>Add PII redaction with Prompt Guard</a>
            {/* A link, never a purchase control: add-ons are contact-sales only. */}
          </Callout>
        )}
        <PolicyRow
          label="Redaction level"
          resolved={doc.redaction.level}
          overridden={draft.redactionLevel !== undefined}
          readOnly={readOnly || !guard}
          reason={!guard ? "Available with Prompt Guard." : undefined}
        >
          <FormField
            as="select"
            label="Redaction level"
            value={draft.redactionLevel ?? doc.redaction.level}
            disabled={readOnly || !guard}
            // The value is read HERE, not inside the updater: React releases the synthetic
            // event when the handler returns, and a functional updater runs during the next
            // render — at which point currentTarget is null.
            onChange={(e) => {
              const value = e.currentTarget.value;
              setDraft((d) => ({ ...d, redactionLevel: value }));
            }}
          >
            <option value="off">off</option>
            <option value="standard">standard</option>
            <option value="strict">strict</option>
          </FormField>
        </PolicyRow>

        <PolicyRow
          label="Guard mandatory"
          resolved={String(doc.guard.mandatory)}
          overridden={draft.guardMandatory !== undefined}
          readOnly={readOnly || !guard}
          reason={!guard ? "Available with Prompt Guard." : undefined}
        />

        {/* Permanently read-only: the contract restricts `true` to enterprise and describes
            it as an org-level ceiling on de-tokenizable redaction. Rendering it as editable
            would be a lie for 100% of self-serve orgs. */}
        <PolicyRow
          label="Reversible redaction"
          resolved={String(doc.guard.reversible)}
          overridden={false}
          readOnly
          reason="Enterprise only."
        />
      </Section>

      <Section title="Models">
        <PolicyRow
          label="Allowed models"
          resolved={`${doc.models.allowed.length} allowed`}
          overridden={draft.modelsAllowed !== undefined}
          readOnly={readOnly}
        />
        <PolicyRow
          label="Default model"
          // `default` is nullable in the contract. "No default" is the honest rendering of
          // a real null; substituting the first allowed model would invent a policy.
          resolved={doc.models.default ?? "No default"}
          overridden={draft.modelsDefault !== undefined}
          readOnly={readOnly}
        >
          <FormField
            as="select"
            label="Default model"
            value={draft.modelsDefault ?? doc.models.default ?? ""}
            disabled={readOnly}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setDraft((d) => ({ ...d, modelsDefault: value }));
            }}
          >
            {/* A null default is a real state, not a missing value — it means the pipeline
                picks per request. It is offered explicitly rather than silently coerced. */}
            <option value="">No default</option>
            {doc.models.allowed.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </FormField>
        </PolicyRow>
      </Section>

      <Section title="Providers">
        <PolicyRow
          label="Allowed providers"
          resolved={doc.providers.allowed.join(", ")}
          overridden={draft.providersAllowed !== undefined}
          readOnly={readOnly || !enterprise}
          reason={!enterprise ? "Provider restrictions are on Enterprise." : undefined}
        />
      </Section>

      <Section title="Surfaces">
        {(["browser_ext", "public_api", "mcp", "vscode", "antigravity", "codex"] as const).map((s) => (
          <PolicyRow
            key={s}
            label={SURFACE_NAMES[s] ?? s}
            resolved={`${doc.surfaces[s]?.disabledActions?.length ?? 0} actions disabled`}
            overridden={false}
            readOnly={readOnly}
          />
        ))}
      </Section>

      <div className="pg-form__actions">
        <Button variant="secondary" onClick={() => setDraft({})} disabled={changeCount === 0}>
          Discard changes
        </Button>
        {/* ONE save for the whole screen, not per-row: policy is a document with one version
            counter, and ten autosaves are ten audit rows and ten chances at a 409. */}
        <Button
          disabled={changeCount === 0 || save.isPending || readOnly}
          onClick={async () => {
            const overrides = toOverrides(draft, doc);
            try {
              await save.mutateAsync({ overrides, version: doc.version });
              setDraft({});
              toast.show("Policy saved.");
            } catch {
              /* rendered above; values are kept so nothing the admin typed is lost */
            }
          }}
        >
          {save.isPending ? "Saving…" : changeCount ? `Save policy · ${changeCount} changes` : "Save policy"}
        </Button>
        <Button variant="ghost" onClick={() => setShowHistory(true)}>
          View history
        </Button>
        <Button variant="ghost" onClick={() => setConfirmReset(true)} disabled={readOnly}>
          Reset all
        </Button>
      </div>

      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}

      <Modal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Reset all settings to plan defaults?"
        description="Every setting your team has pinned goes back to your plan's default. This creates a new version — it does not delete previous ones."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await reset.mutateAsync();
                setConfirmReset(false);
                toast.show("Policy reset to plan defaults.");
              }}
            >
              Reset all
            </Button>
          </>
        }
      />
    </div>
  );
}

const SURFACE_NAMES: Record<string, string> = {
  browser_ext: "Browser extension",
  public_api: "Public API",
  mcp: "MCP",
  vscode: "VS Code",
  antigravity: "Antigravity",
  codex: "Codex",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={title} titleAs="h2">
      {children}
    </Card>
  );
}

/**
 * Every row renders BOTH facts: the resolved value (what is actually enforced right now) and
 * whether it is inherited or pinned.
 *
 * Without that, an admin who clears a field cannot tell whether they set it to empty or
 * released it back to the plan default — and those do very different things.
 */
function PolicyRow({
  label,
  resolved,
  overridden,
  readOnly,
  reason,
  children,
}: {
  label: string;
  resolved: string;
  overridden: boolean;
  readOnly?: boolean;
  reason?: string;
  children?: React.ReactNode;
}) {
  const id = `policy-${label.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <div className="pg-row">
      <div>
        <p className="pg-row__label">{label}</p>
        {children ?? <p className="pg-row__help">{resolved}</p>}
        {reason && (
          <p id={`${id}-reason`} className="pg-disabled-reason">
            {reason}
          </p>
        )}
      </div>
      {/* Text, not colour alone, and referenced from the control's description so assistive
          tech hears "Overridden" together with the value. */}
      <Badge tone={overridden ? "accent" : "neutral"} id={`${id}-badge`}>
        {overridden ? "● Overridden" : "Plan default"}
      </Badge>
      {readOnly && <span className="pg-visually-hidden">read only</span>}
    </div>
  );
}

/**
 * The reconcile screen. Two admins editing the same policy is not a rare case in a team
 * product; it is a Tuesday.
 *
 * "They changed" comes from the audit trail, which is PATHS ONLY — we can honestly say which
 * settings changed and who changed them; we cannot show what to. There is no auto-merge:
 * two admins disagreeing about an allow-list is a conversation, not a three-way diff.
 */
function ConflictNotice({ doc, onReview }: { doc: GetOrgPolicyResponse; onReview: () => void }) {
  const audit = usePolicyAudit(true);
  const theirs = audit.data?.[0];
  const keys = theirs?.changed_keys ?? [];
  return (
    <Callout tone="warn" title="Someone else changed this policy" role="alert">
      <p>
        {keys.length ? `${keys.length} setting${keys.length === 1 ? "" : "s"} changed` : "This policy changed"}{" "}
        since you opened this page. Your changes haven&apos;t been saved yet — nothing was
        overwritten.
      </p>
      {keys.length > 0 && <p>They changed: {keys.join(" · ")}</p>}
      <p className="pg-disabled-reason">You&apos;re editing version {doc.version}.</p>
      <Button size="sm" variant="secondary" onClick={onReview}>
        Review their version
      </Button>
    </Callout>
  );
}

/** Paths and counts only, never values — the contract's own logging rule. */
function HistoryModal({ onClose }: { onClose: () => void }) {
  const audit = usePolicyAudit(true);
  const rows = audit.data ?? [];
  return (
    <Modal open onClose={onClose} title="Policy history" size="lg">
      {audit.isPending ? (
        <Skeleton height={160} />
      ) : (
        <DataTable
          caption="Policy change history"
          rowKey={(r) => `${r.to_version}-${r.changed_at}`}
          rows={rows}
          columns={[
            {
              key: "version",
              header: "Version",
              rowHeader: true,
              cell: (r) => `Version ${r.to_version}`,
            },
            { key: "at", header: "When", cell: (r) => <DateText iso={r.changed_at} /> },
            {
              key: "how",
              header: "How",
              // `source` distinguishes an admin edit from a tier re-derivation triggered by
              // a plan change. Without it, "policy.tier_rederived" looks like a colleague.
              cell: (r) => (r.source === "event" ? "Automatic" : "An admin"),
            },
            {
              key: "keys",
              header: "What changed",
              cell: (r) => (r.changed_keys.length ? r.changed_keys.join(" · ") : "—"),
            },
          ]}
          empty={<p>No changes yet — your team is on the plan defaults.</p>}
        />
      )}
      {/*
        [CORRECTION] The design spec's audit mock shows an email per row. The contract
        returns `actor_sub_hash` — a hash, not an address. Rendering it would echo a
        SERVER-SUPPLIED identifier the user never typed, and would be meaningless to them
        besides. So rows say WHAT changed and WHEN, and the "who" column says whether it was
        an admin or an automatic re-derivation. That is everything the contract actually
        knows.
      */}
      <p className="pg-row__help">
        We record which settings changed and when, never the values.
      </p>
    </Modal>
  );
}

/**
 * Build the override document.
 *
 * NOTE: this is a PUT of the full remaining override set, not a PATCH. Deep-merge cannot
 * express deletion, so releasing a key back to its plan default has to be a replace — and
 * mixing the two would make "revert" silently do nothing.
 */
function toOverrides(draft: Draft, doc: GetOrgPolicyResponse): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (draft.redactionLevel !== undefined) {
    out.redaction = { ...doc.redaction, level: draft.redactionLevel };
  }
  if (draft.guardMandatory !== undefined) {
    out.guard = { ...doc.guard, mandatory: draft.guardMandatory };
  }
  if (draft.modelsAllowed !== undefined || draft.modelsDefault !== undefined) {
    out.models = {
      ...doc.models,
      ...(draft.modelsAllowed ? { allowed: draft.modelsAllowed } : {}),
      ...(draft.modelsDefault ? { default: draft.modelsDefault } : {}),
    };
  }
  if (draft.providersAllowed !== undefined) {
    out.providers = { ...doc.providers, allowed: draft.providersAllowed };
  }
  return out;
}

export { toOverrides };
export type { Draft };

/** Exported for the memo lint; keeps the module's shape stable for tests. */
export const POLICY_V1_KEYS = [
  "redaction.level",
  "redaction.policies",
  "guard.mandatory",
  "models.allowed",
  "models.default",
  "providers.allowed",
  "surfaces.*.disabledActions",
] as const;

/** Keys deliberately excluded from v1, with the reason, so nobody adds one by accident. */
export const POLICY_V1_EXCLUDED: Readonly<Record<string, string>> = {
  "guard.reversible": "Contract restricts true to enterprise; editable would be a lie for self-serve orgs.",
  actions: "Server-owned semantics across three resolved tiers, with no preview of the effect.",
  clarify: "Pipeline tuning, not policy.",
  caching: "Pipeline tuning, not policy.",
  allowedDomains: "Only meaningful for read_page, a surface the portal does not explain.",
  refreshIntervalSec: "An ops knob wearing a customer's clothes.",
  schema_version: "Server-owned; the contract says it MUST NOT appear in overrides.",
  version: "Server-owned.",
  org: "Server-owned.",
  tier: "Server-owned.",
  degraded: "Server-owned.",
};
